import { Product } from '@components/ui/Product';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/shadcn/button';
import { Label } from '@shared/components/ui/shadcn/label';
import { TextArea } from '@shared/components/ui/textarea';
import { AlertTriangle, Package } from 'lucide-react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Cart, StockLocationCart } from '../../models';

export interface CreateLocationOrderModalRef {
  open: (cart: Cart, receptionId: number) => void;
  close: () => void;
}

interface CreateLocationOrderModalProps {
  onConfirm: (cartId: number, notes?: string) => Promise<void>;
}

// Función auxiliar para construir URL completa de imagen
const buildImageUrl = (imageHash?: string) => {
  if (!imageHash) return '';
  if (imageHash.startsWith('http')) return imageHash;
  return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageHash}`;
};

export const CreateLocationOrderModal = forwardRef<CreateLocationOrderModalRef, CreateLocationOrderModalProps>(
  ({ onConfirm }, ref) => {
    const modalRef = useRef<ModalCenterRef>(null);
    const [cart, setCart] = useState<Cart | null>(null);
    const [receptionId, setReceptionId] = useState<number | null>(null);
    const [notes, setNotes] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    useImperativeHandle(ref, () => ({
      open: (selectedCart: Cart, selectedReceptionId: number) => {
        setCart(selectedCart);
        setReceptionId(selectedReceptionId);
        setNotes('');
        modalRef.current?.openModal();
      },
      close: () => {
        modalRef.current?.closeModal();
        setCart(null);
        setReceptionId(null);
        setNotes('');
      },
    }));

    const handleConfirm = async () => {
      if (!cart) return;

      setIsCreating(true);
      try {
        await onConfirm(cart.id, notes.trim() || undefined);
        modalRef.current?.closeModal();
        setCart(null);
        setReceptionId(null);
        setNotes('');
      } catch (error) {
        console.error('Error creating location order:', error);
      } finally {
        setIsCreating(false);
      }
    };

    // Filtrar productos del carrito que pertenecen a esta recepción
    const receptionProducts =
      cart?.stock_location_carts?.filter((slc: StockLocationCart) => slc.returned_reception_id === receptionId) || [];

    const totalProducts = receptionProducts.length;
    const totalQuantity = receptionProducts.reduce((sum, slc) => sum + slc.qty, 0);

    return (
      <ModalCenter ref={modalRef} title="Crear Orden de Ubicación" type="warning" width="xl">
        <div className="space-y-6">
          {/* Advertencia */}
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-amber-900 dark:text-amber-100 mb-1">Advertencia Importante</h4>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Al crear esta orden de ubicación, el carrito <span className="font-mono font-bold">{cart?.code}</span>{' '}
                  se bloqueará y <strong>no podrás seguir añadiendo pedidos ni productos</strong> en este carrito.
                  Tendrás que escanear un nuevo carrito para continuar.
                </p>
              </div>
            </div>
          </div>

          {/* Resumen del carrito */}
          <div className="bg-background border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-primary" />
              <h4 className="font-semibold text-foreground">Resumen del Carrito</h4>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm text-muted-foreground">Código del Carrito</p>
                <p className="text-lg font-mono font-bold text-foreground">{cart?.code}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Productos</p>
                <p className="text-lg font-bold text-foreground">
                  {totalProducts} producto{totalProducts !== 1 ? 's' : ''} ({totalQuantity} unidades)
                </p>
              </div>
            </div>

            {/* Lista de productos */}
            {receptionProducts.length > 0 && (
              <div className="border-t border-border pt-4">
                <p className="text-sm font-medium text-foreground mb-3">Productos a ubicar:</p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {receptionProducts.map((slc) => (
                    <div key={slc.id} className="flex items-center justify-between bg-muted/50 rounded-lg p-2">
                      <div className="flex-1 min-w-0">
                        <Product
                          id={slc.product?.id || 0}
                          src={buildImageUrl(slc.product?.image || slc.product?.image_hash)}
                          name={slc.product?.name || `Producto ID: ${slc.product_id}`}
                          code={slc.product?.code || ''}
                          acronym={slc.product?.name?.charAt(0).toUpperCase()}
                          showImageModal={true}
                          enableLineClamp={true}
                        />
                      </div>
                      <span className="ml-4 text-sm font-medium text-foreground flex-shrink-0">{slc.qty} ud.</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Notas opcionales */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notas (opcional)</Label>
            <TextArea
              id="notes"
              placeholder="Añade notas sobre esta orden de ubicación..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
              rows={3}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">{notes.length}/500 caracteres</p>
          </div>

          {/* Botones */}
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => modalRef.current?.closeModal()} disabled={isCreating}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isCreating || totalProducts === 0}
              className="bg-primary hover:bg-primary/90 text-white"
            >
              {isCreating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creando...
                </>
              ) : (
                'Crear Orden de Ubicación'
              )}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

CreateLocationOrderModal.displayName = 'CreateLocationOrderModal';
